import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Box, Paper, Typography, Button, Breadcrumbs, Menu, MenuItem } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import AddIcon from "@mui/icons-material/Add";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap/dist/js/bootstrap.bundle.min.js";
import DataTable from "datatables.net-bs5";
import $ from "jquery";
import "datatables.net-bs5/css/dataTables.bootstrap5.min.css";
import "select2/dist/css/select2.min.css";
import "select2";
import Swal from "sweetalert2";
import "./RaiseComplaint.css";
import "./BusinessVerticalMasterManagement.css";
import { productVerticalMasterAPI, masterDataAPI } from "../services/api";
import { getRoutePermissions } from "../utils/auth";
import ExcelJS from "exceljs";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { getDataTableExportRows } from "../utils/dataTableExportUtils";

const extractMarketsList = (marketRes) => {
  if (!marketRes) return [];
  if (marketRes?.success && marketRes?.data) {
    const payload = marketRes.data;
    if (Array.isArray(payload.markets)) return payload.markets;
    if (Array.isArray(payload.Markets)) return payload.Markets;
  }
  if (Array.isArray(marketRes?.data?.Markets)) return marketRes.data.Markets;
  if (Array.isArray(marketRes?.data?.markets)) return marketRes.data.markets;
  if (Array.isArray(marketRes?.data)) return marketRes.data;
  return [];
};

const normalizeMarket = (item) => ({
  marketCode: item.marketCode || item.MarketCode || item.marketcode || "",
  marketName: item.marketName || item.MarketName || item.marketname || "",
});

const normalizeProductVertical = (item) => {
  let bvId = "";
  if (item.bvid !== undefined && item.bvid !== null) {
    bvId = String(item.bvid);
  } else if (item.bvId !== undefined && item.bvId !== null) {
    bvId = String(item.bvId);
  } else if (item.bVID !== undefined && item.bVID !== null) {
    bvId = String(item.bVID);
  } else if (item.BVID !== undefined && item.BVID !== null) {
    bvId = String(item.BVID);
  } else if (item.catgId !== undefined && item.catgId !== null) {
    bvId = String(item.catgId);
  } else if (item.CatgId !== undefined && item.CatgId !== null) {
    bvId = String(item.CatgId);
  }

  return {
    bvCode:
      item.bvCode || item.BVCode || item.BVCODE || item.catgCode || item.CatgCode || "",
    bvId,
    bvName:
      item.bvName || item.BVName || item.BVNAME || item.catgName || item.CatgName || "",
    marketCode: item.marketCode || item.MarketCode || item.MARKETCODE || "",
    marketName: item.marketName || item.MarketName || item.MARKETNAME || "",
    isActive: item.isActive ?? item.IsActive ?? false,
    createdOn: item.createdOn || item.CreatedOn || null,
  };
};

const formatDateTime = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();
  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");
  return `${day}-${month}-${year} ${hours}:${minutes}`;
};

const BusinessVerticalMasterManagement = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [filters, setFilters] = useState({
    search: "",
    status: "all",
    marketCode: "",
  });
  const [productVerticals, setProductVerticals] = useState([]);
  const [markets, setMarkets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pageNumber] = useState(1);
  const [pageSize] = useState(50);
  const [totalRecords, setTotalRecords] = useState(0);
  const [exportMenuAnchor, setExportMenuAnchor] = useState(null);
  const tableRef = useRef(null);
  const dataTableInstanceRef = useRef(null);
  const statusSelectRef = useRef(null);
  const marketSelectRef = useRef(null);

  const routePermissions = getRoutePermissions(location.pathname);
  const { canView, canAdd, canEdit } = routePermissions;

  const currentUser = (() => {
    try {
      const u = localStorage.getItem("user");
      return u ? JSON.parse(u) : null;
    } catch (e) {
      return null;
    }
  })();

  useEffect(() => {
    if (!canView) {
      Swal.fire({
        title: "Access Denied",
        text: "You do not have permission to view this page.",
        icon: "error",
        confirmButtonText: "OK",
      }).then(() => navigate("/dashboard"));
      return;
    }
    loadMarkets();
    fetchProductVerticals();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canView]);

  // Initialize Select2 for Status dropdown
  useEffect(() => {
    if (statusSelectRef.current) {
      const $select = $(statusSelectRef.current);

      if ($select.data("select2")) {
        $select.select2("destroy");
      }

      $select.select2({
        width: "100%",
        minimumResultsForSearch: Infinity,
      });

      $select.on("change", function (e) {
        handleFilterChange("status", e.target.value);
      });

      return () => {
        if ($select.data("select2")) {
          $select.select2("destroy");
        }
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Update Select2 value when filters.status changes
  useEffect(() => {
    if (statusSelectRef.current && $(statusSelectRef.current).data("select2")) {
      const $select = $(statusSelectRef.current);
      const currentValue = $select.val();
      if (currentValue !== filters.status) {
        $select.off("change");
        $select.val(filters.status).trigger("change.select2");
        $select.on("change", function (e) {
          handleFilterChange("status", e.target.value);
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.status]);

  // Initialize Select2 for Market dropdown
  useEffect(() => {
    if (marketSelectRef.current && markets.length > 0) {
      const $select = $(marketSelectRef.current);

      if ($select.data("select2")) {
        $select.select2("destroy");
      }

      $select.select2({
        width: "100%",
        placeholder: "All Markets",
        allowClear: true,
      });

      $select.on("change", function (e) {
        handleFilterChange("marketCode", e.target.value || "");
      });

      return () => {
        if ($select.data("select2")) {
          $select.select2("destroy");
        }
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [markets]);

  // Update Select2 value when filters.marketCode changes
  useEffect(() => {
    if (
      marketSelectRef.current &&
      $(marketSelectRef.current).data("select2")
    ) {
      const $select = $(marketSelectRef.current);
      const currentValue = $select.val();
      if (currentValue !== filters.marketCode) {
        $select.off("change");
        $select.val(filters.marketCode || "").trigger("change.select2");
        $select.on("change", function (e) {
          handleFilterChange("marketCode", e.target.value || "");
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.marketCode]);

  useEffect(() => {
    if (!tableRef.current) return;

    // If table already exists, update it with new data
    if (dataTableInstanceRef.current) {
      try {
        dataTableInstanceRef.current.clear();
        dataTableInstanceRef.current.rows.add(productVerticals);
        dataTableInstanceRef.current.draw();
        console.log("DataTable updated with", productVerticals.length, "rows");
        return;
      } catch (e) {
        console.log("Error updating table, will recreate:", e);
        dataTableInstanceRef.current.destroy();
        dataTableInstanceRef.current = null;
      }
    }

    // Only initialize if we have data or if we want to show empty state
    console.log("Initializing DataTable with data:", productVerticals);

    try {
      if (!$.fn.DataTable) {
        DataTable($);
      }

      dataTableInstanceRef.current = $(tableRef.current).DataTable({
        data: productVerticals,
        paging: true,
        searching: true,
        info: true,
        lengthChange: true,
        lengthMenu: [5, 10, 25, 50, 100],
        dom: "lfrtip",
        ordering: true,
        destroy: true,
        scrollX: true,
        autoWidth: false,
        columns: [
          {
            title: "SNo.",
            data: null,
            render: (d, type, row, meta) =>
              (pageNumber - 1) * pageSize + meta.row + 1,
            className: "text-center",
          },
          {
            title: "Business Vertical ID",
            data: "bvId",
            defaultContent: "-",
            visible: false,
            render: (d, type, row) => {
              if (type === "display") {
                // d should contain the bvId value from data: "bvId"
                // Check all possible variations
                const id = d || row?.bvId || row?.bvid || row?.bVID || row?.BVID || "";
                // Only log first few rows to avoid spam
                if (row && !row._logged && id && id.trim() !== "") {
                  console.log("Rendering Business Vertical ID - row:", row, "d:", d, "final id:", id);
                  row._logged = true;
                }
                return id || "-";
              }
              return d || row?.bvId || row?.bvid || row?.bVID || row?.BVID || "";
            },
          },
          { 
            title: "Business Vertical Code", 
            data: "bvCode",
            visible: false
          },
          { title: "Business Vertical Name", data: "bvName" },
          {
            title: "Market",
            data: "marketName",
            render: (d, type, row) => d || row.marketName || row.marketCode || "-",
          },
          {
            title: "Created On",
            data: "createdOn",
            render: (d) => formatDateTime(d),
          },
          {
            title: "Status",
            data: null,
            orderable: false,
            render: (d) => {
              const isActive = d.isActive === true || d.isActive === 1;
              const toggleClass = isActive ? "active" : "inactive";
              const toggleIcon = isActive ? "bi-toggle-on" : "bi-toggle-off";
              if (!canEdit) {
                return `<div class="d-flex justify-content-center">
                  <span title="${isActive ? "Active" : "Inactive"}" style="background:transparent;border:none;padding:0;font-size:24px;color:${isActive ? "#4caf50" : "#9e9e9e"};cursor:default;">
                    <i class="bi ${toggleIcon}"></i>
                  </span>
                </div>`;
              }
              return `<div class="d-flex justify-content-center">
                <button class="btn btn-sm status-toggle ${toggleClass}" 
                  data-code="${d.bvCode}" 
                  data-active="${isActive}"
                  title="${
                    isActive ? "Click to deactivate" : "Click to activate"
                  }"
                  style="background: transparent; 
                         border: none; 
                         padding: 0;
                         font-size: 24px;
                         color: ${isActive ? "#4caf50" : "#9e9e9e"};
                         cursor: pointer;
                         transition: all 0.3s ease;">
                  <i class="bi ${toggleIcon}"></i>
                </button>
              </div>`;
            },
          },
        ],
        initComplete: function () {
          try {
            const wrapper = $(tableRef.current).closest(
              ".dataTables_wrapper, .dt-container"
            );
            if (wrapper.length > 0) {
              const lengthEl = wrapper.find(".dt-length");
              const searchEl = wrapper.find(".dt-search");
              if (lengthEl.length > 0 || searchEl.length > 0) {
                let topWrap = wrapper.find(".dtFltrTop");
                if (topWrap.length === 0) {
                  topWrap = $("<div class='dtFltrTop d-flex flex-wrap'></div>");
                  wrapper.prepend(topWrap);
                }
                lengthEl.each(function () {
                  $(this).appendTo(topWrap);
                });
                searchEl.each(function () {
                  $(this).appendTo(topWrap);
                });
              }

              const infoEl = wrapper.find(".dt-info");
              const pagingEl = wrapper.find(".dt-paging");
              if (infoEl.length > 0 || pagingEl.length > 0) {
                let bottomWrap = wrapper.find(".dtFltrBotm");
                if (bottomWrap.length === 0) {
                  bottomWrap = $(
                    "<div class='dtFltrBotm d-flex flex-wrap'></div>"
                  );
                  wrapper.append(bottomWrap);
                }
                infoEl.each(function () {
                  $(this).appendTo(bottomWrap);
                });
                pagingEl.each(function () {
                  $(this).appendTo(bottomWrap);
                });
              }
            }
          } catch (error) {
            console.error("Error in initComplete:", error);
          }
        },
      });

      if (canEdit) {
        $(tableRef.current)
          .off("click", ".status-toggle")
          .on("click", ".status-toggle", function () {
            const code = $(this).data("code");
            const currentActive =
              $(this).data("active") === true ||
              $(this).data("active") === "true" ||
              $(this).data("active") === 1;
            handleStatusToggle(code, !currentActive);
          });
      } else {
        $(tableRef.current).off("click", ".status-toggle");
      }
    } catch (err) {
      console.error("DataTable init error", err);
    }

    return () => {
      if (dataTableInstanceRef.current) {
        dataTableInstanceRef.current.destroy();
        dataTableInstanceRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productVerticals, canEdit]);

  const loadMarkets = async () => {
    try {
      const response = await masterDataAPI.getMarkets("", 1, 500);
      const marketList = extractMarketsList(response);
      setMarkets(marketList.map(normalizeMarket).filter((m) => m.marketCode));
    } catch (e) {
      console.error("Error loading markets:", e);
    }
  };

  const fetchProductVerticals = async (
    search = filters.search,
    status = filters.status,
    marketCode = filters.marketCode
  ) => {
    setLoading(true);
    try {
      const response = await productVerticalMasterAPI.getPaged({
        searchText: search || undefined,
        status: status === "all" ? undefined : status,
        marketCode: marketCode || undefined,
        pageNumber: pageNumber,
        pageSize: pageSize,
      });

      console.log("API Response:", response);
      
      if (response?.success && response.data) {
        const payload = response.data;
        const items = payload.productVerticals || payload.ProductVerticals || [];
        console.log("Raw items from API:", items);
            console.log("First item sample:", items[0]);
        
        const normalizedItems = items.map((bv, index) => {
          const normalized = normalizeProductVertical(bv);
          // Log items with bvid values
          if (bv.bvid && bv.bvid.trim() !== "") {
            console.log(`Item ${index} with bvid value - Original bvid: "${bv.bvid}", Normalized bvId: "${normalized.bvId}"`);
          }
          if (index === 0) {
            console.log("First item - Original:", bv);
            console.log("First item - Normalized:", normalized);
            console.log("BVID check - bvid:", bv.bvid, "bvId:", bv.bvId, "bVID:", bv.bVID, "BVID:", bv.BVID);
          }
          return normalized;
        });
        
        console.log("Normalized items:", normalizedItems);
        const itemsWithBvId = normalizedItems.filter(item => item.bvId && item.bvId.trim() !== "");
        console.log("Items with bvId values:", itemsWithBvId);
        console.log("Sample items with bvId:", itemsWithBvId.slice(0, 3).map(item => ({ bvCode: item.bvCode, bvId: item.bvId })));
        // Hide special "Default Category (Default Market)" / 0000 row from grid
        const visibleItems = normalizedItems.filter((item) => {
          const code = String(item.bvCode || "").trim();
          const name = String(item.bvName || "").trim().toUpperCase();
          const marketCode = String(item.marketCode || "").trim();
          const marketName = String(item.marketName || "").trim().toUpperCase();
          if (code === "0000") return false;
          if (name === "DEFAULT CATEGORY" || name === "DEFAULT BV") return false;
          if (marketCode === "0000" && marketName === "DEFAULT MARKET") return false;
          return true;
        });
        setProductVerticals(visibleItems);
        setTotalRecords(payload.totalRecords || payload.TotalRecords || 0);
      } else {
        console.warn("No data in response:", response);
        setProductVerticals([]);
        setTotalRecords(0);
      }
    } catch (e) {
      console.error("Error fetching business verticals:", e);
      setProductVerticals([]);
      Swal.fire(
        "Error",
        "Failed to load business verticals. Please try again.",
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const resetFilters = () => {
    setFilters({ search: "", status: "all", marketCode: "" });
    fetchProductVerticals("", "all", "");
  };

  const handleStatusToggle = async (bvCode, newActive) => {
    const actionText = newActive ? "activate" : "deactivate";
    const result = await Swal.fire({
      title: "Confirm Status Change",
      text: `Are you sure you want to ${actionText} this business vertical?`,
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: newActive ? "#4caf50" : "#ff9800",
      cancelButtonColor: "#6c757d",
      confirmButtonText: newActive ? "Yes, activate!" : "Yes, deactivate!",
      cancelButtonText: "Cancel",
    });

    if (result.isConfirmed) {
      try {
        const modifiedBy = currentUser?.userIdCode || "SYSADMIN001";
        const response = await productVerticalMasterAPI.updateStatus(
          bvCode,
          newActive,
          modifiedBy
        );

        if (response?.success) {
          Swal.fire(
            "Success!",
            response.message || `Business Vertical has been ${actionText}d.`,
            "success"
          );
          fetchProductVerticals();
        } else {
          Swal.fire(
            "Error",
            response?.message || `Failed to ${actionText} business vertical`,
            "error"
          );
        }
      } catch (e) {
        console.error(`Error ${actionText}ing business vertical:`, e);
        Swal.fire(
          "Error",
          e.response?.data?.message ||
            `Failed to ${actionText} business vertical. Please try again.`,
          "error"
        );
      }
    }
  };

  const handleDelete = async (bvCode) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "This will deactivate the business vertical.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, proceed!",
    });

    if (result.isConfirmed) {
      try {
        const modifiedBy = currentUser?.userIdCode || "SYSADMIN001";
        const response = await productVerticalMasterAPI.delete(bvCode, modifiedBy);

        if (response?.success) {
          Swal.fire(
            "Updated!",
            response.message || "Business Vertical has been deactivated.",
            "success"
          );
          fetchProductVerticals();
        } else {
          Swal.fire(
            "Error",
            response?.message || "Failed to update business vertical",
            "error"
          );
        }
      } catch (e) {
        console.error("Error deleting business vertical:", e);
        Swal.fire(
          "Error",
          e.response?.data?.message ||
            "Failed to update business vertical. Please try again.",
          "error"
        );
      }
    }
  };

  // Export to PDF
  const exportToPDF = () => {
    // Export what the grid is actually showing: when a search is active
    // only the matching rows are written out, otherwise the full data set.
    const exportRows = getDataTableExportRows(dataTableInstanceRef, productVerticals);
    try {
      if (!exportRows || exportRows.length === 0) {
        Swal.fire("Info", "No data to export", "info");
        return;
      }

      const doc = new jsPDF();
      doc.setFontSize(16);
      doc.text("Business Vertical Master Data", 14, 15);

      const tableData = exportRows.map((vertical, index) => [
        String(index + 1),
        String(vertical.bvName || "-"),
        String(vertical.marketName || vertical.marketCode || "-"),
        String(formatDateTime(vertical.createdOn) || "-"),
        vertical.isActive ? "Active" : "Inactive",
      ]);

      autoTable(doc, {
        head: [["SNo.", "Business Vertical Name", "Market", "Created On", "Status"]],
        body: tableData,
        startY: 25,
        styles: { fontSize: 8 },
        headStyles: { fillColor: [102, 126, 234] },
        margin: { top: 25 },
      });

      doc.save(`Product_Vertical_Master_${new Date().toISOString().split("T")[0]}.pdf`);
    } catch (error) {
      console.error("Error exporting to PDF:", error);
      Swal.fire({
        title: "Error",
        text: `Failed to export PDF: ${error.message || "Unknown error"}. Please check the browser console for details.`,
        icon: "error",
      });
    }
  };

  // Export to CSV
  const exportToCSV = () => {
    // Export what the grid is actually showing: when a search is active
    // only the matching rows are written out, otherwise the full data set.
    const exportRows = getDataTableExportRows(dataTableInstanceRef, productVerticals);
    try {
      const headers = ["SNo.", "Business Vertical Name", "Market", "Created On", "Status"];
      const csvData = exportRows.map((vertical, index) => [
        index + 1,
        vertical.bvName || "",
        vertical.marketName || vertical.marketCode || "",
        formatDateTime(vertical.createdOn),
        vertical.isActive ? "Active" : "Inactive",
      ]);

      const csvContent = [
        headers.join(","),
        ...csvData.map((row) => row.map((cell) => `"${cell}"`).join(",")),
      ].join("\n");

      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      const url = URL.createObjectURL(blob);
      link.setAttribute("href", url);
      link.setAttribute(
        "download",
        `Product_Vertical_Master_${new Date().toISOString().split("T")[0]}.csv`
      );
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error("Error exporting to CSV:", error);
      Swal.fire("Error", "Failed to export CSV", "error");
    }
  };

  // Export to Excel
  const exportToExcel = async () => {
    // Export what the grid is actually showing: when a search is active
    // only the matching rows are written out, otherwise the full data set.
    const exportRows = getDataTableExportRows(dataTableInstanceRef, productVerticals);
    try {
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet("Business Vertical Master");

      // Add title
      worksheet.mergeCells("A1:E1");
      const titleRow = worksheet.getRow(1);
      titleRow.getCell(1).value = "Business Vertical Master Data";
      titleRow.getCell(1).font = { size: 16, bold: true };
      titleRow.getCell(1).alignment = { horizontal: "center", vertical: "middle" };
      titleRow.height = 25;

      // Add headers
      const headerRow = worksheet.getRow(3);
      headerRow.values = ["SNo.", "Business Vertical Name", "Market", "Created On", "Status"];
      headerRow.font = { bold: true };
      headerRow.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "FF667EEA" },
      };
      headerRow.alignment = { horizontal: "center", vertical: "middle" };
      headerRow.height = 20;

      // Add data
      exportRows.forEach((vertical, index) => {
        const row = worksheet.addRow([
          index + 1,
          vertical.bvName || "",
          vertical.marketName || vertical.marketCode || "",
          formatDateTime(vertical.createdOn),
          vertical.isActive ? "Active" : "Inactive",
        ]);
        row.alignment = { vertical: "middle" };
      });

      // Set column widths
      worksheet.columns.forEach((column) => {
        column.width = 20;
      });

      // Generate buffer and download
      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const link = document.createElement("a");
      const url = URL.createObjectURL(blob);
      link.setAttribute("href", url);
      link.setAttribute(
        "download",
        `Product_Vertical_Master_${new Date().toISOString().split("T")[0]}.xlsx`
      );
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error("Error exporting to Excel:", error);
      Swal.fire("Error", "Failed to export Excel", "error");
    }
  };

  // Handle export menu
  const handleExportMenuOpen = (event) => {
    setExportMenuAnchor(event.currentTarget);
  };

  const handleExportMenuClose = () => {
    setExportMenuAnchor(null);
  };

  const handleExportOption = (format) => {
    handleExportMenuClose();
    if (format === "excel") {
      exportToExcel();
    } else if (format === "csv") {
      exportToCSV();
    } else if (format === "pdf") {
      exportToPDF();
    }
  };

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Box>
          <Breadcrumbs
            aria-label="breadcrumb"
            className="add-edit-product-breadcrumb"
          >
            <Typography
              color="text.primary"
              className="breadcrumb-current brdcrmbLstitms brdcrmbCurPg"
            >
              Business Vertical Master
            </Typography>
          </Breadcrumbs>
          <Typography variant="body2" sx={{ color: "#666666", mt: 1 }}>
            Manage business vertical master.
          </Typography>
        </Box>
        <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
          {/* Export Button with Dropdown */}
          <Button
            onClick={handleExportMenuOpen}
            sx={{
              background: "transparent",
              border: "1px solid #667eea",
              color: "#667eea",
              fontWeight: 500,
              padding: "8px 10px",
              borderRadius: "6px",
              transition: "all 0.3s ease",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              minWidth: "36px",
              height: "36px",
              "&:hover": {
                background: "transparent",
                borderColor: "#764ba2",
                color: "#764ba2",
              },
              "& .MuiSvgIcon-root": {
                color: "#667eea",
              },
              "&:hover .MuiSvgIcon-root": {
                color: "#764ba2",
              },
            }}
            title="Export"
          >
            <FileDownloadIcon sx={{ fontSize: "26px", filter: "drop-shadow(0 0 0.5px currentColor)" }} />
            <ArrowDropDownIcon sx={{ fontSize: "16px", ml: 0.5 }} />
          </Button>
          <Menu
            anchorEl={exportMenuAnchor}
            open={Boolean(exportMenuAnchor)}
            onClose={handleExportMenuClose}
            anchorOrigin={{
              vertical: "bottom",
              horizontal: "right",
            }}
            transformOrigin={{
              vertical: "top",
              horizontal: "right",
            }}
          >
            <MenuItem onClick={() => handleExportOption("excel")}>
              Excel
            </MenuItem>
            <MenuItem onClick={() => handleExportOption("csv")}>
              CSV
            </MenuItem>
            <MenuItem onClick={() => handleExportOption("pdf")}>
              PDF
            </MenuItem>
          </Menu>
          {/* {canAdd && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => navigate("/product-verticals/add")}
              sx={{
                background: "linear-gradient(135deg, #dc3545 0%, #ff0022 100%)",
                border: "none",
                color: "white",
                fontWeight: 600,
                fontSize: "11px",
                boxShadow: "0 4px 15px rgba(245, 87, 108, 0.4)",
                borderRadius: "4px",
                textTransform: "uppercase",
                padding: "8px 24px",
                "&:hover": {
                  background: "linear-gradient(135deg, #dc3545 0%, #ff0022 100%)",
                  boxShadow: "0 6px 20px rgba(245, 87, 108, 0.6)",
                  transform: "translateY(-2px)",
                },
                transition: "all 0.3s ease",
              }}
            >
              Add Business Vertical
            </Button>
          )} */}
        </Box>
      </Box>

      {/* Filter Panel — hidden for now; uncomment when needed again
      <div className="card-panel mb-3 filter-panel">
        <div
          className="section-heading d-flex align-items-center gap-2 mb-3"
          style={{ marginBottom: "14px" }}
        >
          <SearchIcon
            fontSize="small"
            className="text-muted"
            style={{
              width: "18px",
              height: "18px",
              flexShrink: 0,
              marginTop: "2px",
            }}
          />
          <span>Filter</span>
        </div>

        <div className="container-fluid">
          <div className="row g-3 align-items-end">
            <div className="col-12 col-md-6 col-lg-4 srchDvwrpr">
              <label className="input-label d-block mb-1 text-start">
                Search
              </label>
              <div className="input-icon" style={{ position: "relative" }}>
                <input
                  type="text"
                  className="form-control form-control-light text-start"
                  placeholder="Search by Code / Name"
                  value={filters.search}
                  onChange={(e) => handleFilterChange("search", e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === "Enter") {
                      handleFilterChange("search", filters.search.trim());
                      fetchProductVerticals(
                        filters.search.trim(),
                        filters.status,
                        filters.marketCode
                      );
                    }
                  }}
                  style={{ height: "42px", textAlign: "left" }}
                />
                <SearchIcon
                  className="icon-right"
                  style={{
                    position: "absolute",
                    right: "12px",
                    top: "50%",
                    transform: "translateY(-50%)",
                    pointerEvents: "none",
                    width: "16px",
                    height: "16px",
                    color: "#6c757d",
                  }}
                />
              </div>
            </div>

            <div className="col-12 col-md-6 col-lg-3">
              <label className="input-label d-block mb-1 text-start">
                Market
              </label>
              <select
                ref={marketSelectRef}
                id="market-filter-select"
                className="form-select form-control-light filter-select text-start"
                value={filters.marketCode}
                style={{ height: "42px", textAlign: "left" }}
              >
                <option value="">All Markets</option>
                {markets.map((m) => (
                  <option key={m.marketCode} value={m.marketCode}>
                    {m.marketName}
                  </option>
                ))}
              </select>
            </div>

            <div className="col-12 col-md-6 col-lg-2">
              <label className="input-label d-block mb-1 text-start">
                Status
              </label>
              <select
                ref={statusSelectRef}
                id="status-filter-select"
                className="form-select form-control-light filter-select text-start"
                value={filters.status}
                style={{ height: "42px", textAlign: "left" }}
              >
                <option value="all">All</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>

            <div className="col-12 col-md-6 col-lg-2">
              <label
                className="input-label d-block mb-1 text-start"
                style={{ visibility: "hidden" }}
              >
                Actions
              </label>
              <div className="d-flex gap-2 filter-buttons justify-content-start">
                <button
                  type="button"
                  className="btn btn-filter"
                  onClick={() => {
                    handleFilterChange("search", filters.search.trim());
                    fetchProductVerticals(
                      filters.search.trim(),
                      filters.status,
                      filters.marketCode
                    );
                  }}
                  style={{
                    background:
                      "linear-gradient(135deg, #7a00f5  0%, #0f00f5 100%)",
                    border: "none",
                    color: "white",
                    fontWeight: 600,
                    boxShadow: "0 4px 15px rgba(102, 126, 234, 0.4)",
                    borderRadius: "4px",
                    padding: "8px 24px",
                    transition: "all 0.3s ease",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background =
                      "linear-gradient(135deg, #0f00f5 0%, #0f00f5 100%)";
                    e.currentTarget.style.transform = "translateY(-2px)";
                    e.currentTarget.style.boxShadow =
                      "0 6px 20px rgba(102, 126, 234, 0.6)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background =
                      "linear-gradient(135deg, #7a00f5  0%, #0f00f5 100%)";
                    e.currentTarget.style.transform = "translateY(0)";
                    e.currentTarget.style.boxShadow =
                      "0 4px 15px rgba(102, 126, 234, 0.4)";
                  }}
                  onMouseDown={(e) => {
                    e.currentTarget.style.transform = "translateY(0)";
                  }}
                  onMouseUp={(e) => {
                    e.currentTarget.style.transform = "translateY(-2px)";
                  }}
                >
                  <SearchIcon fontSize="small" className="me-1" />
                  Search
                </button>
                <button
                  type="button"
                  className="btn btn-reset-filter"
                  onClick={resetFilters}
                  title="Reset"
                  style={{
                    background:
                      "linear-gradient(135deg, #84fab0 0%, #8fd3f4 100%)",
                    border: "none",
                    color: "white",
                    fontWeight: 600,
                    boxShadow: "0 4px 15px rgba(132, 250, 176, 0.4)",
                    borderRadius: "4px",
                    padding: "8px 16px",
                    transition: "all 0.3s ease",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = "translateY(-2px)";
                    e.currentTarget.style.boxShadow =
                      "0 6px 20px rgba(132, 250, 176, 0.6)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = "translateY(0)";
                    e.currentTarget.style.boxShadow =
                      "0 4px 15px rgba(132, 250, 176, 0.4)";
                  }}
                  onMouseDown={(e) => {
                    e.currentTarget.style.transform = "translateY(0)";
                  }}
                  onMouseUp={(e) => {
                    e.currentTarget.style.transform = "translateY(-2px)";
                  }}
                >
                  <RestartAltIcon fontSize="small" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
      */}

      <Paper
        sx={{
          p: 0,
          backgroundColor: "transparent",
          boxShadow: "none",
          border: "none",
        }}
      >
        <div className="card-panel mt-0">
          {loading && productVerticals.length === 0 ? (
            <Box sx={{ textAlign: "center", py: 4 }}>
              <Typography>Loading business verticals...</Typography>
            </Box>
          ) : productVerticals.length === 0 ? (
            <Box sx={{ textAlign: "center", py: 4 }}>
              <Typography variant="body1" color="text.secondary">
                No business verticals found. Click "Add Business Vertical" to create a new business vertical.
              </Typography>
            </Box>
          ) : (
            <div className="table-responsive">
              <table
                ref={tableRef}
                className="table table-striped table-bordered complaint-table custmTblLst"
                style={{ width: "100%" }}
              />
            </div>
          )}
        </div>
      </Paper>
    </Box>
  );
};

export default BusinessVerticalMasterManagement;



